// Three social-proof surfaces backed by the shared activity feed:
//   - LiveActivityTicker (top sticky)
//   - LiveActivityToast  (bottom-right popper)
//   - LiveActivityWidget (Home dashboard sidebar)
import { useEffect, useMemo, useRef, useState } from "react";
import { ACTIVITY_VISUAL, relativeTime, useActivityFeed } from "@/lib/activityApi";
import { useAppSettings } from "@/lib/appSettings";

// iter146 — Mobile sanity: hide the ticker + bottom-right toast on routes
// where they fight the sticky Save bar / BottomNav / on-screen keyboard.
// Returns {hideTicker, hideToast} based on viewport width + current path.
function useMobileSuppress() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.matchMedia("(max-width: 767px)").matches : false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia("(max-width: 767px)");
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  const dataEntry = ["/daily", "/funds", "/mdcf", "/"].includes(path);
  return {
    hideTicker: isMobile,
    hideToast: isMobile && dataEntry,
    isMobile,
  };
}

// ---------- Top sticky ticker ----------
// Rotates through the latest 10 events one at a time:
//   - Visible for 3 seconds (animated slide-in)
//   - Hidden for 12 seconds (the ticker bar collapses to height 0)
//   - Cycle continues — next message after 15 s total.
// This calm, attention-respecting rhythm replaced the always-on marquee.
export function LiveActivityTicker() {
  const settings = useAppSettings();
  const { feed, stats } = useActivityFeed();
  const { hideTicker } = useMobileSuppress();
  // Defensive filter — only rows with the required string fields. Anything
  // missing message/schoolName would crash `.replace()` below. This is a
  // belt-and-braces guard in case a writer ever inserts a malformed row
  // into db.activities (we hit this exact bug in iter 90 when the Razorpay
  // audit log wrote into the wrong collection).
  const rows = useMemo(
    () => feed.filter((r) => r && typeof r.message === "string" && typeof r.schoolName === "string").slice(0, 10),
    [feed],
  );
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (rows.length === 0) {
      setVisible(false);
      return undefined;
    }
    let timer;
    const tick = () => {
      setVisible(true);
      timer = setTimeout(() => {
        setVisible(false);
        // Schedule the NEXT show 12 s later, then advance the index.
        timer = setTimeout(() => {
          setIdx((i) => (i + 1) % rows.length);
        }, 12000);
      }, 3000);
    };
    // Kick the first cycle immediately when rows arrive / idx changes.
    tick();
    return () => clearTimeout(timer);
  }, [idx, rows.length]);

  // Admin can hide the ticker globally via /admin/app/settings.
  if (!settings.liveActivityTickerEnabled) return null;
  // iter146 — hide on mobile; cramped under the fixed AppHeader.
  if (hideTicker) return null;
  if (rows.length === 0) return null;
  const current = rows[idx % rows.length];
  const vis = current ? (ACTIVITY_VISUAL[current.type] || { icon: "✨" }) : null;

  return (
    <div
      data-testid="live-activity-ticker"
      className={`relative w-full overflow-hidden border-b border-clay/30 bg-gradient-to-r from-clay/10 via-moss/10 to-clay/10 text-xs text-ink-900 transition-all duration-500 ${
        visible ? "max-h-[44px] opacity-100" : "max-h-0 opacity-0 border-b-0"
      }`}
      aria-hidden={!visible}
    >
      <div className="flex items-center gap-3 px-3 py-1.5 sm:px-4">
        <span className="hidden shrink-0 items-center gap-1 rounded-full bg-clay px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-white sm:inline-flex">
          🔥 LIVE
        </span>
        {stats && (
          <span
            data-testid="live-active-counter"
            className="hidden shrink-0 items-center gap-1 rounded-full bg-moss/15 px-2 py-0.5 text-[0.65rem] font-semibold text-moss-dark sm:inline-flex"
            title={`${stats.activeSchools24h} schools active in the last 24 hours`}
          >
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-moss"></span>
            {stats.activeSchools24h} {stats.activeSchools24h === 1 ? "school" : "schools"} active now
          </span>
        )}
        <div className="ticker-track relative flex-1 overflow-hidden">
          {current && vis && (
            <span
              key={`${current.id}-${idx}`}
              data-testid="live-activity-current"
              className="ticker-slide inline-flex items-center gap-1.5 text-[0.78rem] whitespace-nowrap"
            >
              <span>{vis.icon}</span>
              <b className="text-ink-900">{current.schoolName}</b>
              <span className="text-ink-700">{current.message.replace(current.schoolName, "").trim()}</span>
              <span className="text-ink-700/70">· {relativeTime(current.createdAt)}</span>
            </span>
          )}
        </div>
      </div>
      <style>{`
        @keyframes tickerSlide {
          0%   { opacity: 0; transform: translateY(-6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .ticker-slide { animation: tickerSlide 320ms cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
}

// ---------- Bottom-right toast popper ----------
// Pops ONE new event at a time, holds for 6s, fades out, then pulls the next
// from the queue. Subtle slide-in animation. Click to dismiss instantly.
export function LiveActivityToast() {
  const settings = useAppSettings();
  const { newEvents, ack } = useActivityFeed();
  const [current, setCurrent] = useState(null);
  const timerRef = useRef(null);
  const { hideToast, isMobile } = useMobileSuppress();

  useEffect(() => {
    if (current || newEvents.length === 0) return;
    setCurrent(newEvents[0]);
    // iter146 — 4s on mobile (less intrusive), keep 6s on desktop.
    const dwellMs = isMobile ? 4000 : 6000;
    timerRef.current = setTimeout(() => {
      ack(newEvents[0].id);
      setCurrent(null);
    }, dwellMs);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEvents, current]);

  // Admin can hide the bottom-right popper independently of the ticker / widget.
  if (!settings.liveActivityToastEnabled) return null;
  // iter146 — hide on mobile data-entry pages (/daily, /funds, /mdcf, /) where
  // the sticky Save bar + BottomNav own the bottom of the screen.
  if (hideToast) return null;
  if (!current) return null;
  const vis = ACTIVITY_VISUAL[current.type] || { icon: "✨", color: "clay" };
  // iter146 — on mobile, pin to TOP under the AppHeader instead of bottom-right.
  const posCls = isMobile
    ? "fixed left-3 right-3 z-[60] overflow-hidden rounded-xl border border-clay/40 bg-white shadow-[0_18px_36px_-12px_rgba(176,93,37,0.35)] animate-toast-in cursor-pointer"
    : "fixed bottom-4 right-4 z-[60] w-[min(360px,calc(100vw-2rem))] cursor-pointer overflow-hidden rounded-xl border border-clay/40 bg-white shadow-[0_18px_36px_-12px_rgba(176,93,37,0.35)] animate-toast-in";
  const posStyle = isMobile
    ? { top: "calc(56px + env(safe-area-inset-top, 0px) + 8px)" }
    : undefined;
  return (
    <div
      data-testid="live-activity-toast"
      className={posCls}
      style={posStyle}
      onClick={() => { if (timerRef.current) clearTimeout(timerRef.current); ack(current.id); setCurrent(null); }}
    >
      <div className="flex items-start gap-3 p-4">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-${vis.color}/15 text-2xl`}>
          {vis.icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-clay">{vis.label} · {relativeTime(current.createdAt)}</p>
          <p className="mt-0.5 truncate text-sm font-semibold text-ink-900">{current.schoolName}</p>
          {current.district && <p className="text-xs text-ink-700">{current.district}</p>}
          <p className="mt-1 text-xs text-ink-700">{(current.message || "").replace(current.schoolName || "", "").trim()}</p>
        </div>
      </div>
      <div className="h-1 w-full bg-clay/15">
        <div className="h-full animate-toast-bar bg-clay" />
      </div>
      <style>{`
        @keyframes toastIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-toast-in { animation: toastIn 280ms cubic-bezier(0.16, 1, 0.3, 1); }
        @keyframes toastBar {
          0% { width: 100%; }
          100% { width: 0%; }
        }
        .animate-toast-bar { animation: toastBar 6s linear forwards; }
      `}</style>
    </div>
  );
}

// ---------- Home dashboard widget ----------
// Sticky side card showing the 8 most recent events + headline counters. Auto
// refreshes via the same poll.
export function LiveActivityWidget() {
  const settings = useAppSettings();
  const { feed, stats } = useActivityFeed();
  // Same defensive filter as LiveActivityTicker — only rows with a real
  // `message` + `schoolName` so .replace() never sees `undefined`.
  const recent = useMemo(
    () => feed.filter((r) => r && typeof r.message === "string" && typeof r.schoolName === "string").slice(0, 8),
    [feed],
  );

  if (!settings.liveActivityWidgetEnabled) return null;

  return (
    <section
      data-testid="live-activity-widget"
      className="overflow-hidden rounded-xl border border-clay/30 bg-white shadow-[0_1px_0_rgba(44,53,40,0.04)]"
    >
      <div className="flex items-center justify-between border-b border-clay/20 bg-gradient-to-r from-clay/10 via-moss/10 to-clay/5 px-4 py-3">
        <div>
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-clay">🔥 Live Activity</p>
          <p className="mt-0.5 text-xs text-ink-700">Updated every 30s</p>
        </div>
        {stats && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full bg-moss px-2.5 py-1 text-[0.7rem] font-semibold text-white"
            title="Schools active in the last 24 hours"
            data-testid="live-active-counter-widget"
          >
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-white"></span>
            {stats.activeSchools24h} active now
          </span>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-2 border-b border-border bg-sand-50 px-4 py-3 text-xs">
          <CounterChip label="Schools joined" value={stats.totalSchools} icon="🏫" />
          <CounterChip label="MDM bills this month" value={stats.mdmBillsThisMonth} icon="📊" />
          <CounterChip label="UCs this month" value={stats.ucsThisMonth} icon="📄" />
          <CounterChip label="Vouchers this month" value={stats.vouchersThisMonth} icon="🧾" />
        </div>
      )}

      <ul className="max-h-[420px] divide-y divide-border overflow-y-auto" data-testid="live-activity-list">
        {recent.length === 0 && (
          <li className="px-4 py-6 text-center text-xs text-ink-700">
            No live activity yet — be the first today!
          </li>
        )}
        {recent.map((ev) => {
          const vis = ACTIVITY_VISUAL[ev.type] || { icon: "✨" };
          return (
            <li
              key={ev.id}
              data-testid={`live-activity-item-${ev.id}`}
              className="flex items-start gap-3 px-4 py-3 text-sm transition-colors hover:bg-sand-50"
            >
              <div className="text-xl leading-none">{vis.icon}</div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-ink-900">{ev.schoolName}</p>
                {ev.district && <p className="text-[0.7rem] text-ink-700">{ev.district}</p>}
                <p className="text-xs text-ink-700">{ev.message.replace(ev.schoolName, "").trim()}</p>
                <p className="mt-0.5 text-[0.65rem] uppercase tracking-[0.14em] text-clay">{relativeTime(ev.createdAt)}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CounterChip({ label, value, icon }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-white px-2 py-1.5">
      <span className="text-base">{icon}</span>
      <div className="min-w-0 leading-tight">
        <p className="text-[1.05rem] font-bold text-ink-900 font-mono">{value}</p>
        <p className="text-[0.62rem] uppercase tracking-[0.12em] text-ink-700">{label}</p>
      </div>
    </div>
  );
}
